import type { App } from 'vue'
import type { Router } from 'vue-router'
import * as Sentry from '@sentry/vue'
import type { ErrorEvent, Contexts } from '@sentry/vue'
import { createLogger } from './logger'
import {
  scrubObject,
  scrubHeaders,
  scrubUrl,
  scrubQueryString,
} from './piiScrubber'

const logger = createLogger('Sentry')

/**
 * Filter errors that should not be sent to Sentry
 *
 * @param event - Sentry error event
 * @returns True if the event should be dropped
 */
function shouldFilterError(event: ErrorEvent): boolean {
  // Filter browser extension errors
  if (event.exception?.values?.[0]?.stacktrace?.frames) {
    const frames = event.exception.values[0].stacktrace.frames
    const hasExtensionFrame = frames.some(
      frame =>
        frame.filename?.includes('chrome-extension://') ||
        frame.filename?.includes('moz-extension://')
    )
    if (hasExtensionFrame) {
      return true
    }
  }

  // Filter known false positives
  const message = event.message || event.exception?.values?.[0]?.value || ''
  const ignoredMessages = [
    'ResizeObserver loop limit exceeded',
    'Non-Error promise rejection captured',
    'Network request failed', // Handle separately via error boundaries
  ]

  if (ignoredMessages.some(ignored => message.includes(ignored))) {
    return true
  }

  return false
}

/**
 * Scrub PII and sensitive data from Sentry events before sending
 *
 * This is the last line of defense against PII leakage. All error events
 * pass through this function to ensure sensitive data is removed.
 *
 * @param event - Sentry error event
 * @returns Scrubbed event or null to drop the event
 */
function beforeSend(event: ErrorEvent): ErrorEvent | null {
  try {
    // 1. Scrub user data (remove PII, keep ID for tracking)
    if (event.user) {
      const scrubbedUser = {
        id: event.user.id, // Keep ID for tracking
        // Remove email, username, ip_address (all PII)
      }
      event.user = scrubbedUser
    }

    // 2. Scrub request data
    if (event.request) {
      // Scrub headers
      if (event.request.headers) {
        event.request.headers = scrubHeaders(
          event.request.headers as Record<string, string>
        )
      }

      // Scrub query string
      if (event.request.query_string) {
        // Convert query_string to string format if it's not already
        const queryString =
          typeof event.request.query_string === 'string'
            ? event.request.query_string
            : new URLSearchParams(
                event.request.query_string as Record<string, string>
              ).toString()
        event.request.query_string = scrubQueryString(queryString)
      }

      // Scrub URL
      if (event.request.url) {
        event.request.url = scrubUrl(event.request.url)
      }

      // Scrub request data/body
      if (event.request.data) {
        event.request.data = scrubObject(event.request.data)
      }
    }

    // 3. Scrub context data
    if (event.contexts) {
      const scrubbedContexts: Record<string, unknown> = {}

      for (const [key, value] of Object.entries(event.contexts)) {
        scrubbedContexts[key] = scrubObject(value)
      }

      event.contexts = scrubbedContexts as Contexts
    }

    // 4. Scrub extra data
    if (event.extra) {
      event.extra = scrubObject(event.extra)
    }

    // 5. Scrub breadcrumbs
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map(breadcrumb => ({
        ...breadcrumb,
        data: breadcrumb.data ? scrubObject(breadcrumb.data) : undefined,
        message: breadcrumb.message, // Keep message as-is (usually safe)
      }))
    }

    // 6. Filter noisy errors
    if (shouldFilterError(event)) {
      return null // Drop event
    }

    // 7. Log in development for verification
    if (import.meta.env.DEV) {
      logger.debug('📤 Sending to Sentry (scrubbed):', event)
    }

    return event
  } catch (error) {
    // CRITICAL: Never throw in beforeSend
    logger.error('Error in beforeSend hook:', error)

    // Return original event (better to send unscrubbed than crash)
    return event
  }
}

/**
 * Initialize Sentry error tracking for the application
 * Only initializes if VITE_SENTRY_DSN is configured
 *
 * @param app - Vue application instance
 * @param router - Vue router instance for performance tracking
 */
export function initSentry(app: App, router: Router): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  // Skip initialization if DSN is not configured
  if (!dsn || dsn.trim() === '') {
    logger.info(
      'Sentry DSN not configured, skipping error tracking initialization'
    )
    return
  }

  try {
    Sentry.init({
      app,
      dsn,
      environment: import.meta.env.MODE,
      release: __APP_VERSION__,
      integrations: [Sentry.browserTracingIntegration({ router })],
      // Performance Monitoring (limited support in GlitchTip)
      tracesSampleRate: 0.1, // Capture 10% of transactions
      // PII Scrubbing - Last line of defense before sending to Sentry
      beforeSend,
    })

    logger.info(
      `Sentry initialized for ${import.meta.env.MODE} environment (release: ${__APP_VERSION__})`
    )
  } catch (error) {
    logger.error('Failed to initialize Sentry:', error)
  }
}
